export const getCommerceItemPrerequisites = (field) =>
  Array.isArray(field?.commerceItemPrerequisites)
    ? field.commerceItemPrerequisites.filter(
        (fieldName) => typeof fieldName === "string" && fieldName.length > 0,
      )
    : [];

export const getCommerceItemHidePrerequisites = (field) =>
  Array.isArray(field?.commerceItemHidePrerequisites)
    ? field.commerceItemHidePrerequisites.filter(
        (fieldName) => typeof fieldName === "string" && fieldName.length > 0,
      )
    : [];

const getValidCommerceItemRules = (
  field,
  additionalFields,
  getConfiguredRules,
) => {
  const commerceItemNames = new Set(
    additionalFields
      .filter(
        (candidate) =>
          candidate?.fieldType === "CommerceItem" &&
          candidate.fieldName &&
          !candidate.fieldAdminOnly &&
          candidate.fieldName !== field?.fieldName,
      )
      .map((candidate) => candidate.fieldName),
  );

  return getConfiguredRules(field).filter((fieldName) =>
    commerceItemNames.has(fieldName),
  );
};

const hasSelectedCommerceItem = (fieldNames, registration) =>
  fieldNames.some(
    (fieldName) => Number(registration?.[fieldName] || 0) > 0,
  );

export const isAdditionalFieldVisible = (
  field,
  registration = {},
  additionalFields = [],
) => {
  if (field?.fieldHideOnWaitlist && registration?.status === "Waitlist") {
    return false;
  }

  const configuredPrerequisites = getCommerceItemPrerequisites(field);
  const validPrerequisites = getValidCommerceItemRules(
    field,
    additionalFields,
    getCommerceItemPrerequisites,
  );
  const showRuleSatisfied =
    configuredPrerequisites.length === 0 ||
    hasSelectedCommerceItem(validPrerequisites, registration);
  const validHidePrerequisites = getValidCommerceItemRules(
    field,
    additionalFields,
    getCommerceItemHidePrerequisites,
  );
  const hiddenBySelectedItem = hasSelectedCommerceItem(
    validHidePrerequisites,
    registration,
  );

  return showRuleSatisfied && !hiddenBySelectedItem;
};

export const isCommerceItemVisible = isAdditionalFieldVisible;

const clearedFieldValue = (field) => {
  if (field?.fieldType === "Checkbox") return false;
  if (
    field?.fieldType === "CommerceItem" ||
    field?.fieldType === "Numberbox"
  ) {
    return 0;
  }
  return "";
};

const clearValueIfPresent = (registration, fieldName, value) => {
  if (
    !Object.prototype.hasOwnProperty.call(registration, fieldName) ||
    registration[fieldName] === value
  ) {
    return false;
  }

  registration[fieldName] = value;
  return true;
};

export const clearHiddenAdditionalFieldValues = (
  additionalFields = [],
  registration = {},
) => {
  const normalizedRegistration = { ...registration };
  let clearedAValue;

  do {
    clearedAValue = false;

    for (const field of additionalFields) {
      if (
        !field?.fieldName ||
        isAdditionalFieldVisible(
          field,
          normalizedRegistration,
          additionalFields,
        )
      ) {
        continue;
      }

      const hadCommerceSelection =
        field.fieldType === "CommerceItem" &&
        Number(normalizedRegistration[field.fieldName] || 0) !== 0;
      clearedAValue =
        clearValueIfPresent(
          normalizedRegistration,
          field.fieldName,
          clearedFieldValue(field),
        ) || clearedAValue;

      if (field.fieldType === "CommerceItem") {
        for (const suffix of ["Cost", "TaxCost", "SubCost"]) {
          const costFieldName = `${field.fieldName}${suffix}`;
          if (
            hadCommerceSelection &&
            !Object.prototype.hasOwnProperty.call(
              normalizedRegistration,
              costFieldName,
            )
          ) {
            normalizedRegistration[costFieldName] = 0;
            clearedAValue = true;
            continue;
          }
          clearedAValue =
            clearValueIfPresent(
              normalizedRegistration,
              costFieldName,
              0,
            ) || clearedAValue;
        }
      }
    }
  } while (clearedAValue);

  return normalizedRegistration;
};

export const clearHiddenCommerceItemSelections =
  clearHiddenAdditionalFieldValues;
